import { useState, useEffect, useMemo } from 'react'
import { Reorder } from 'framer-motion'
import { useDebounce } from 'use-debounce'

import { useMutation, gql } from '@apollo/client'
import { Text } from '@chakra-ui/react'

import BuildpackComp from './Buildpack'
import AddBuildpackModal from './AddBuildpackModal'

const UPDATE_BUILDPACKS_ORDER_MUTATION = gql`
  mutation UpdateBuildpacksOrder($orders: [BuildPackOrderInput!]!) {
    updateBuildPacksOrder(orders: $orders) {
      id
    }
  }
`

export type Buildpack = {
  id: string
  order: number
  source: string
}

const Buildpacks = ({
  buildpacks = [],
  refetchData,
  addModal: { isOpen: isAddModalOpen, onClose },
}: {
  buildpacks: Buildpack[]
  refetchData: () => void
  addModal: {
    isOpen: boolean
    onClose: () => void
  }
}) => {
  const [state, setState] = useState<Buildpack[]>(buildpacks)

  const [debouncedValue] = useDebounce(state, 1000)

  const orders = useMemo(() => {
    return debouncedValue.map((value) => `${value.source}`).join('-')
  }, [debouncedValue])

  const [updateBuildpacksOrder] = useMutation(UPDATE_BUILDPACKS_ORDER_MUTATION)

  useEffect(() => {
    if (buildpacks.length && !state.length) {
      setState(buildpacks.slice().sort((a, b) => a.order - b.order))
    }
  }, [buildpacks, state, setState])

  const saveChanges = async () => {
    const variables = {
      orders: debouncedValue.map((buildpack) => ({
        id: buildpack.id,
        order: buildpack.order,
      })),
    }

    // Send mutation
    await updateBuildpacksOrder({ variables })

    // Refetch query
    await refetchData()

    // NOTE: Reset state to trigger the other useEffect and populate state with new data
    setState([])
  }

  // Only mutate changes if a change was actually made
  useEffect(() => {
    saveChanges()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders])

  return (
    <>
      {state.length ? (
        <Reorder.Group
          axis="y"
          onReorder={(values) => {
            const valuesWithNewOrder = values.map((value, i) => ({
              ...value,
              order: i,
            }))
            setState(valuesWithNewOrder)
          }}
          values={state}
          style={{ width: '100%' }}
        >
          {state.map((buildpack) => (
            <BuildpackComp
              key={buildpack.source}
              buildpack={buildpack}
              refetchData={async () => {
                await refetchData()
                setState([])
              }}
            />
          ))}
        </Reorder.Group>
      ) : (
        <Text>There are no buildpacks associated with your app yet.</Text>
      )}
      <AddBuildpackModal
        isOpen={isAddModalOpen}
        onClose={onClose}
        refetchData={async () => {
          await refetchData()
          setState([])
        }}
      />
    </>
  )
}

export default Buildpacks
